// frontend/src/components/Requisitions/edit/components/EditStepGeneral.jsx

import React from "react";

import LoadingSpinner from "../../../UI/LoadingSpinner";
import SelectField from "./SelectField";
import StatusHistory from "./StatusHistory";

import { displayStatus, fmtMoney, getLabelDefault } from "../utils";

const getIdDefault = (row) => row?.id;

function getOptionLabel(option) {
  if (!option) return "";

  if (typeof option === "string" || typeof option === "number") {
    return String(option);
  }

  return (
    getLabelDefault(option) ||
    option.label ||
    option.name ||
    option.description ||
    option.text ||
    (option.id ? `ID: ${option.id}` : "")
  );
}

function resolveHeaderDisplayValue({ key, value, catalogs, buildHeaderExtraOption }) {
  if (value === null || typeof value === "undefined" || value === "") return "—";

  const options = Array.isArray(catalogs?.[key]) ? catalogs[key] : [];
  const found = options.find((row) => String(getIdDefault(row)) === String(value));
  if (found) return getOptionLabel(found);

  const extra = typeof buildHeaderExtraOption === "function" ? buildHeaderExtraOption(key) : null;
  if (extra && String(getIdDefault(extra)) === String(value)) {
    return getOptionLabel(extra);
  }

  return `ID: ${value}`;
}

export default function EditStepGeneral({
  loadingStep1,
  catalogs,
  catalogMeta,
  headerForm,
  invalidHeaderRefs,
  buildHeaderExtraOption,
  onChangeHeader,
  realAmountBase,
  loadingMe,
  showRealAmountSection,
  statusHistory,
  busyItemOp,
  busySave,
  busyRealAmount,
  onCancel,
  onNext,

  canEditRequisition = false,
  canEditHeader = false,
  canManageRealAmounts = false,
  currentStatusLabel = "",
  editBlockedMessage = "",
  disabledHeaderFields = [],
}) {
  const catalogKeys = Object.keys(catalogMeta || {});
  const isBusy = busyItemOp || busySave || busyRealAmount;
  const disabledHeaderFieldSet = new Set(disabledHeaderFields || []);

  return (
    <section className="bg-white rounded-xl shadow p-4 md:p-6">
      <h2 className="text-lg font-semibold mb-4">Información general</h2>

      {!!editBlockedMessage && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-800 rounded p-3 text-sm">
          <div className="font-semibold">Edición bloqueada</div>
          <div className="mt-1">{editBlockedMessage}</div>
        </div>
      )}

      {!canEditHeader && canEditRequisition && (
        <div className="mb-4 border border-blue-200 bg-blue-50 text-blue-900 rounded p-3 text-sm">
          <div className="font-semibold">Encabezado en modo solo lectura</div>
          <div className="mt-1">
            Tu perfil puede modificar la requisición, pero los campos del Paso 1 se muestran solo como texto.
          </div>
        </div>
      )}

      {loadingStep1 ? (
        <LoadingSpinner />
      ) : (
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          {invalidHeaderRefs.length > 0 && canEditHeader && (
            <div className="md:col-span-2 border border-amber-300 bg-amber-50 text-amber-900 rounded p-3 text-sm">
              <div className="font-semibold">
                Hay catálogos ya no disponibles en esta requisición.
              </div>
              <div className="mt-1">
                Antes de guardar, debes volver a seleccionar valores activos para:
              </div>
              <ul className="mt-2 list-disc ml-5">
                {invalidHeaderRefs.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {invalidHeaderRefs.length > 0 && !canEditHeader && (
            <div className="md:col-span-2 border border-slate-300 bg-slate-50 text-slate-800 rounded p-3 text-sm">
              <div className="font-semibold">
                Esta requisición contiene referencias de catálogo ya no disponibles.
              </div>
              <div className="mt-1">
                Tu perfil las ve como solo lectura en el Paso 1:
              </div>
              <ul className="mt-2 list-disc ml-5">
                {invalidHeaderRefs.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {catalogKeys.map((key) => {
            const label = catalogMeta[key]?.uiLabel || key;
            const value = String(headerForm[key] ?? "");

            if (canEditHeader) {
              return (
                <SelectField
                  key={key}
                  label={label}
                  value={value}
                  onChange={(nextValue) => onChangeHeader(key, nextValue)}
                  options={catalogs[key] || []}
                  getId={getIdDefault}
                  getLabel={getLabelDefault}
                  placeholder={`Selecciona ${label.toLowerCase()}`}
                  extraOption={buildHeaderExtraOption(key)}
                  disabled={disabledHeaderFieldSet.has(key)}
                />
              );
            }

            const displayValue = resolveHeaderDisplayValue({
              key,
              value,
              catalogs,
              buildHeaderExtraOption,
            });

            return (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <div className="px-2 py-1 border rounded bg-gray-100 text-gray-700 min-h-[38px] flex items-center">
                  {displayValue}
                </div>
              </div>
            );
          })}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Estatus
            </label>
            <div className="px-2 py-1 border rounded bg-gray-100 text-gray-700 min-h-[38px] flex items-center">
              {currentStatusLabel || displayStatus(headerForm.status) || "—"}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Fecha de creación
            </label>
            <div className="px-2 py-1 border rounded bg-gray-100 text-gray-700">
              {headerForm.created_at || "—"}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Motivos de Requisición
            </label>
            <textarea
              rows={4}
              className={`w-full border rounded px-2 py-1 ${
                canEditRequisition ? "" : "bg-gray-100 text-gray-700 cursor-not-allowed"
              }`}
              value={headerForm.requisition_reason}
              onChange={(e) => onChangeHeader("requisition_reason", e.target.value)}
              disabled={!canEditRequisition}
            />
          </div>

          <div className="md:col-span-2 border rounded p-3 bg-gray-50">
            <div className="text-sm">
              <strong>Monto real</strong>: {fmtMoney(realAmountBase)}
            </div>

            <div className="text-[11px] text-gray-500 mt-1">
              {loadingMe
                ? "Verificando permisos..."
                : showRealAmountSection
                  ? canManageRealAmounts
                    ? "Superusuario o superior puede capturarlo por partida en Paso 2."
                    : "No tienes permiso para capturar monto real."
                  : "En licitaciones, el monto real se deriva automáticamente."}
            </div>

            <StatusHistory history={statusHistory} />
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          disabled={isBusy}
          className={`px-4 py-2 rounded ${
            isBusy ? "bg-slate-100" : "bg-slate-200 hover:bg-slate-300"
          }`}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNext?.();
          }}
          className="inline-flex items-center px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Siguiente
        </button>
      </div>
    </section>
  );
}
